package one.healthium.uscreen.ui.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import ifaandroid.core.ui_kit.generated.resources.Res
import ifaandroid.core.ui_kit.generated.resources.ic_checkmark_24
import one.healthium.ui.compose.components.AppIcons
import one.healthium.uscreen.ui.util.clickableHapticOnPress
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.uscreen.domain.models.UScreenGroup
import one.healthium.uscreen.ui.util.parseHexColor
import org.jetbrains.compose.resources.painterResource

private const val COLLAPSED_WEIGHT = 0.15f
private const val EXPANDED_WEIGHT = 1f
private val SEGMENT_HEIGHT = 22.dp
private val SEGMENT_CORNER = 4.dp

@Immutable
data class UScreenProgressState(
    val groups: List<UScreenGroup>,
    val currentGroupIndex: Int,
    val screensCompletedInGroup: Int,
    val totalScreensInGroup: Int,
)

@Composable
fun UScreenProgressBar(
    modifier: Modifier = Modifier,
    state: UScreenProgressState,
    canGoBack: Boolean,
    onBackClicked: () -> Unit,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 10.dp)
            .testTag("uscreen_progress_bar"),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        if (canGoBack) {
            BackButton(onBackClicked)
        }

        state.groups.forEachIndexed { index, group ->
            val groupColor = parseHexColor(group.color) ?: AppTheme.colors.divider
            val isCurrent = index == state.currentGroupIndex
            val isCompleted = index < state.currentGroupIndex

            val targetWeight = if (isCurrent) EXPANDED_WEIGHT else COLLAPSED_WEIGHT
            val animatedWeight by animateFloatAsState(
                targetValue = targetWeight,
                animationSpec = tween(400),
            )

            val fillProgress = when {
                isCompleted -> 1f
                isCurrent && state.totalScreensInGroup > 0 ->
                    state.screensCompletedInGroup.toFloat() / state.totalScreensInGroup
                else -> 0f
            }
            val animatedFill by animateFloatAsState(
                targetValue = fillProgress,
                animationSpec = tween(300),
            )

            Segment(
                modifier = if (isCurrent) Modifier.weight(animatedWeight) else Modifier.size(SEGMENT_HEIGHT),
                group = group,
                groupColor = groupColor,
                fill = animatedFill,
                isExpanded = isCurrent,
                isCompleted = isCompleted,
            )
        }
    }
}

@Composable
private fun RowScope.Segment(
    modifier: Modifier,
    group: UScreenGroup,
    groupColor: Color,
    fill: Float,
    isExpanded: Boolean,
    isCompleted: Boolean,
) {
    Box(
        modifier = modifier
            .then(if (isExpanded) Modifier.height(SEGMENT_HEIGHT) else Modifier)
            .clip(RoundedCornerShape(SEGMENT_CORNER))
            .background(AppTheme.uscreenStyle.progressSegmentTrack),
        contentAlignment = Alignment.Center,
    ) {
        if (fill > 0f) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .fillMaxWidth(fill)
                    .align(Alignment.CenterStart)
                    .background(groupColor),
            )
        }

        when {
            isCompleted -> {
                Icon(
                    painter = painterResource(Res.drawable.ic_checkmark_24),
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier.size(20.dp),
                )
            }
            isExpanded -> {
                // Dark text over the unfilled track portion
                Text(
                    text = group.label,
                    style = AppTheme.textStyles.sublineMedium,
                    color = AppTheme.uscreenStyle.progressContentColor,
                    maxLines = 1,
                )
                // White text clipped to the fill width, visible only over the filled portion
                Box(
                    modifier = Modifier
                        .matchParentSize()
                        .drawWithContent {
                            clipRect(right = size.width * fill) {
                                this@drawWithContent.drawContent()
                            }
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = group.label,
                        style = AppTheme.textStyles.sublineMedium,
                        color = Color.White,
                        maxLines = 1,
                    )
                }
            }
        }
    }
}

@Composable
private fun BackButton(onClick: () -> Unit) {
    Surface(
        modifier = Modifier
            .size(36.dp)
            .testTag("uscreen_back_button"),
        shape = CircleShape,
        color = AppTheme.uscreenStyle.backButtonBackground,
        shadowElevation = 1.dp,
    ) {
        Box(
            modifier = Modifier.clickableHapticOnPress(onClick = onClick),
            contentAlignment = Alignment.Center,
        ) {
            Icon(
                painter = painterResource(AppIcons.icNavigationBack),
                contentDescription = null,
                modifier = Modifier.size(18.dp),
            )
        }
    }
}
